/**
 * NLE Export orchestrators — standalone + embedded.
 *
 * `exportNLEStandalone` drives the standalone pipeline:
 *   1. Planning   — buildNLEExport produces a manifest + FCP7 XML
 *   2. Extracting — per-entry bytes resolved via resolveEntryBytesStandalone
 *   3. Zipping    — streaming zip via fflate, one entry at a time
 *   4. Saving     — browser download via URL.createObjectURL
 *
 * `exportNLEEmbedded` drives the embedded pipeline:
 *   1. Planning   — same buildNLEExport, but peeks audio via sql.js DB
 *   2. Bridge     — ships manifest + XML to Unity via bridgeExportNLE
 *      (Unity handles extraction, zipping, and native save/share)
 *
 * `exportNLE` is the capability-aware entry point. It dispatches to
 * standalone or embedded based on capabilities.bridge.
 */

import * as fflate from 'fflate';
import type { TimelineProject, AudioClip } from '@/types/timeline.ts';
import { getBlob } from '@/previs/audioBlobStore.ts';
import { buildNLEExport, type NLEExportOptions, type AudioBytePeeker, type NLEManifest } from './nleManifestBuilder.ts';
export type { NLEExportOptions } from './nleManifestBuilder.ts';
import { resolveEntryBytesStandalone } from './mediaExtractor.ts';
import { capabilities } from '@/capabilities.ts';
import { getEmbeddedDatabase, peekAudioBytes } from '@/previs/sqlReader.ts';
import { bridgeExportNLE } from '@/bridge/embeddedBridge.ts';

export type ExportNLEPhase = 'planning' | 'extracting' | 'zipping' | 'saving';

export type ExportNLEHooks = {
  onProgress: (phase: ExportNLEPhase, current: number, total: number) => void;
  isCancelled: () => boolean;
};

export class NLEExportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NLEExportError';
  }
}

export async function exportNLEStandalone(
  project: TimelineProject,
  audioClips: AudioClip[],
  options: NLEExportOptions,
  hooks: ExportNLEHooks,
): Promise<void> {
  // 1. Planning — build manifest + XML using a standalone audio peeker
  hooks.onProgress('planning', 0, 1);
  const peeker: AudioBytePeeker = async (clipId, n) => {
    const bytes = getBlob(clipId);
    if (bytes) return bytes.slice(0, n);
    const clip = audioClips.find((c) => c.id === clipId);
    if (!clip?.cdnUrl) return null;
    try {
      const resp = await fetch(clip.cdnUrl, { headers: { Range: `bytes=0-${n - 1}` } });
      return resp.ok ? new Uint8Array(await resp.arrayBuffer()) : null;
    } catch {
      return null;
    }
  };
  const { manifest, xmlString } = await buildNLEExport(project, audioClips, options, peeker);
  if (hooks.isCancelled()) return;

  // 2. Streaming zip — manifest order, one entry at a time
  const chunks: Uint8Array[] = [];
  let zipErrMsg: string | null = null;
  const zip = new fflate.Zip((err, dat /*, final */) => {
    if (err) zipErrMsg = err.message;
    else if (dat) chunks.push(dat);
  });

  // XML first
  const xmlFile = new fflate.ZipPassThrough(manifest.xmlFilename);
  zip.add(xmlFile);
  xmlFile.push(new TextEncoder().encode(xmlString), true);

  // Then media entries
  const total = manifest.media.length;
  for (let i = 0; i < total; i++) {
    if (hooks.isCancelled()) {
      try { zip.terminate(); } catch { /* terminate may throw if already ended */ }
      return;
    }
    const entry = manifest.media[i];
    const bytes = await resolveEntryBytesStandalone(entry, project, audioClips);
    if (!bytes) {
      try { zip.terminate(); } catch { /* */ }
      throw new NLEExportError(`Failed to resolve ${describeEntry(entry)}`);
    }
    const file = new fflate.ZipPassThrough(entry.target);
    zip.add(file);
    file.push(bytes, true);
    hooks.onProgress('extracting', i + 1, total);
  }

  hooks.onProgress('zipping', 0, 1);
  zip.end();
  if (zipErrMsg) throw new NLEExportError(`Zip failed: ${zipErrMsg}`);

  // 3. Browser download
  hooks.onProgress('saving', 0, 1);
  const blob = new Blob(chunks as BlobPart[], { type: 'application/zip' });
  triggerDownload(blob, `${manifest.zipBaseName}.zip`);
}

function describeEntry(entry: { kind: string; shotId?: number; clipId?: number }): string {
  if (entry.kind === 'audioClip') return `audio clip ${entry.clipId}`;
  return `shot ${entry.shotId} (${entry.kind})`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Size estimator ─────────────────────────────────────────────────────────

/**
 * Estimates the final zip size in MB based on the manifest entries.
 * Used by the size guard before extraction begins. Estimates are rough:
 *   - Shot images: ~250 KB each (typical 1920x1080 JPG at quality 0.92)
 *   - Shot videos: ~8 MB each (rough average for short MP4 clips)
 *   - Audio clips: exact byte length if loaded in audioBlobStore, else ~200 KB
 * Pure function — does no I/O. Safe to call at any time.
 */
export function estimateZipSizeMB(manifest: NLEManifest): number {
  let totalBytes = 0;
  for (const entry of manifest.media) {
    if (entry.kind === 'shotImage') totalBytes += 250_000;
    else if (entry.kind === 'shotVideo') totalBytes += 8_000_000;
    else if (entry.kind === 'audioClip') {
      const blob = getBlob(entry.clipId);
      totalBytes += blob ? blob.byteLength : 200_000;
    }
  }
  return totalBytes / (1024 * 1024);
}

/**
 * Default size threshold (MB) above which the standalone path shows a
 * confirmation dialog before extraction. 250 MB is a conservative cap for
 * a single-session browser download — large enough to cover most projects
 * without prompting, small enough that the browser can comfortably hold
 * the assembled Blob in memory before triggering the download. Tunable.
 */
export const NLE_SIZE_GUARD_THRESHOLD_MB = 250;

// ── Embedded orchestrator ──────────────────────────────────────────────────

/**
 * Embedded-mode NLE export orchestrator.
 *
 * Opens one sql.js Database, peeks audio headers for all clips, builds the
 * manifest + FCP7 XML, then hands both to Unity via bridgeExportNLE.
 * Unity handles extraction, zip assembly, and native save/share.
 *
 * The DB is opened once at the top and closed in `finally` — this avoids
 * fetch-per-clip cost and DB leaks.
 */
export async function exportNLEEmbedded(
  project: TimelineProject,
  audioClips: AudioClip[],
  options: NLEExportOptions,
  hooks: ExportNLEHooks,
): Promise<void> {
  hooks.onProgress('planning', 0, 1);

  const db = await getEmbeddedDatabase();
  try {
    const peeker: AudioBytePeeker = async (clipId, n) => peekAudioBytes(db, clipId, n);
    const { manifest, xmlString } = await buildNLEExport(project, audioClips, options, peeker);
    if (hooks.isCancelled()) return;
    await bridgeExportNLE(xmlString, manifest, project.title, hooks);
  } finally {
    db.close();
  }
}

// ── Capability-aware dispatcher ────────────────────────────────────────────

/**
 * Capability-aware NLE export entry point.
 * Dispatches to embedded or standalone based on capabilities.bridge.
 *
 * Task 14 will replace the Cmd+E call site with the export dialog. Until
 * then, useKeyboardShortcuts.ts calls this directly with hardcoded defaults.
 */
export async function exportNLE(
  project: TimelineProject,
  audioClips: AudioClip[],
  options: NLEExportOptions,
  hooks: ExportNLEHooks,
): Promise<void> {
  if (capabilities.bridge) {
    return exportNLEEmbedded(project, audioClips, options, hooks);
  } else {
    return exportNLEStandalone(project, audioClips, options, hooks);
  }
}
